import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api.js';
import { useMe } from '../auth.js';
import { Button, ErrorText, Input } from '../components/ui.js';
import { eur } from '../format.js';
import { BookkeepingSettings } from './BookkeepingSettings.js';

interface Settings {
  paymentCostsEur: string;
  settlementFrom: string | null;
}

export function SettingsPage() {
  const isAdmin = useMe().user?.role === 'admin';
  const client = useQueryClient();
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api<Settings>('GET', '/api/settings') });
  const [costs, setCosts] = useState<string | null>(null);
  const [from, setFrom] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api<Settings>('PUT', '/api/settings', {
        ...(costs !== null ? { paymentCostsEur: costs } : {}),
        ...(from !== null ? { settlementFrom: from } : {}),
      }),
    onSuccess: async () => {
      setCosts(null);
      setFrom(null);
      await client.invalidateQueries({ queryKey: ['settings'] });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <section className="space-y-6">
      <h1 className="text-xl font-semibold">Settings</h1>
      {settings.data && !isAdmin && (
        <div className="space-y-1 text-sm">
          <p>Default costs per Steam payment: {eur(settings.data.paymentCostsEur)}</p>
          <p>First month settled in this system: {settings.data.settlementFrom ?? 'not set'}</p>
        </div>
      )}
      {settings.data && isAdmin && (
        <form className="max-w-sm space-y-3" onSubmit={submit}>
          <Input
            label="Default costs per Steam payment (EUR)"
            inputMode="decimal"
            value={costs ?? settings.data.paymentCostsEur}
            onChange={(e) => setCosts(e.target.value)}
          />
          <p className="text-xs text-slate-500">Prefilled when a payment is entered and deducted before the payment is split between the partners.</p>
          <Input
            label="First month settled in this system (YYYY-MM)"
            placeholder="2024-02"
            pattern="\d{4}-(0[1-9]|1[0-2])"
            value={from ?? settings.data.settlementFrom ?? ''}
            onChange={(e) => setFrom(e.target.value)}
          />
          <p className="text-xs text-slate-500">Earlier months were paid out before this system and can never be selected for a payment.</p>
          <Button type="submit" disabled={save.isPending || (costs === null && from === null)}>Save</Button>
          {save.isSuccess && costs === null && from === null && <p className="text-sm text-emerald-700 dark:text-emerald-400">Saved.</p>}
          {save.isError && <ErrorText>{save.error instanceof ApiError ? (save.error.message ?? save.error.code) : 'Saving failed'}</ErrorText>}
        </form>
      )}
      {settings.isError && <ErrorText>The settings could not be loaded</ErrorText>}
      <BookkeepingSettings />
    </section>
  );
}
